import { toast } from 'react-toastify';
import React from 'react';

// Define different error types for specific handling
export const ErrorTypes = {
  NETWORK: 'NETWORK',
  API: 'API',
  AUTHENTICATION: 'AUTHENTICATION',
  PERMISSION: 'PERMISSION',
  VALIDATION: 'VALIDATION',
  UNKNOWN: 'UNKNOWN'
};

// Format error message based on error type and details
export function formatErrorMessage(error, defaultMessage = 'An unexpected error occurred') {
  if (!error) return defaultMessage;
  
  // Handle network errors
  if (error.message === 'Failed to fetch') {
    return 'Network connection error. Please check your internet connection and try again.';
  }
  
  // Handle Firebase/Auth errors
  if (error.code && error.code.startsWith('auth/')) {
    return formatAuthError(error);
  }
  
  // Handle API error responses
  if (error.response) {
    return `Error ${error.response.status}: ${error.response.data?.message || 'Server error'}`;
  }
  
  // Return the error message if available, otherwise use default
  return error.message || defaultMessage;
}

// Format authentication errors
function formatAuthError(error) {
  switch (error.code) {
    case 'auth/user-not-found':
    case 'auth/wrong-password':
      return 'Invalid email or password';
    case 'auth/email-already-in-use':
      return 'This email is already in use';
    case 'auth/weak-password':
      return 'Password is too weak';
    case 'auth/requires-recent-login':
      return 'Please log out and log back in to perform this action';
    default:
      return `Authentication error: ${error.message}`;
  }
}

// Get error type based on error details
export function getErrorType(error) {
  if (!error) return ErrorTypes.UNKNOWN;
  
  if (error.message === 'Failed to fetch' || 
      error.name === 'NetworkError' || 
      error.name === 'AbortError') {
    return ErrorTypes.NETWORK;
  }
  
  if (error.code && error.code.startsWith('auth/')) {
    return ErrorTypes.AUTHENTICATION;
  }
  
  if (error.response) {
    const status = error.response.status;
    if (status === 401 || status === 403) {
      return ErrorTypes.PERMISSION;
    }
    if (status === 400 || status === 422) {
      return ErrorTypes.VALIDATION;
    }
    if (status >= 500) {
      return ErrorTypes.API;
    }
  }
  
  return ErrorTypes.UNKNOWN;
}

// Handle error with toast notification
export function handleError(error, customMessage = null) {
  const errorType = getErrorType(error);
  const message = customMessage || formatErrorMessage(error);
  
  console.error('Error occurred:', error);
  
  // Display toast with appropriate styling based on error type
  toast.error(message, {
    position: "top-right",
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    className: `error-toast error-${errorType.toLowerCase()}`
  });
  
  return { message, type: errorType };
}

// Create a wrapper for fetch calls with timeout and error handling
export async function fetchWithTimeout(url, options = {}, timeout = 30000) {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);
  
  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    });
    
    clearTimeout(id);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error = new Error(errorData.message || `Request failed with status ${response.status}`);
      error.response = response;
      error.responseData = errorData;
      throw error;
    }
    
    return response;
  } catch (error) {
    clearTimeout(id);
    
    // Improve the error object for better error handling
    if (error.name === 'AbortError') {
      throw new Error('Request timed out. Please try again.');
    }
    
    throw error;
  }
}

// Create a global error boundary component
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  
  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }
  
  render() {
    if (this.state.hasError) {
      return (
        <div className="error-boundary">
          <h2>Something went wrong</h2>
          <p>{formatErrorMessage(this.state.error)}</p>
          <button 
            onClick={() => this.setState({ hasError: false, error: null })}
            className="btn btn-primary mt-4"
          >
            Try again
          </button>
        </div>
      );
    }
    
    return this.props.children;
  }
} 